import { useState } from "react";
import { useData } from "../context/DataContext";
import {
  FileText,
  TrendingUp,
  Users,
  GraduationCap,
  AlertCircle,
  CheckCircle,
  BarChart3,
  PieChart,
  Download,
} from "lucide-react";
import { calculateLehrpersonStats } from "../utils/calculations";
import { cn } from "../utils/cn";
import { exportToExcel } from "../utils/excelExport";

type ReportType = "workload" | "classes" | "subjects" | "overview";

const Reports = () => {
  const { data } = useData();
  const [selectedReport, setSelectedReport] = useState<ReportType>("overview");

  const reportTypes = [
    {
      type: "overview" as ReportType,
      label: "Gesamtübersicht",
      icon: BarChart3,
      color: "bg-blue-500",
    },
    {
      type: "workload" as ReportType,
      label: "Arbeitsbelastung",
      icon: TrendingUp,
      color: "bg-purple-500",
    },
    {
      type: "classes" as ReportType,
      label: "Klassenanalyse",
      icon: GraduationCap,
      color: "bg-green-500",
    },
    {
      type: "subjects" as ReportType,
      label: "Fachstatistik",
      icon: FileText,
      color: "bg-orange-500",
    },
  ];

  // Calculate statistics
  const totalLehrpersonen = data.lehrpersonen.length;
  const totalKlassen = data.klassen.length;
  const totalZuteilungen = data.zuteilungen.length;
  const totalSchueler = data.klassen.reduce((sum, kl) => sum + kl.bestand, 0);

  const lehrpersonenStats = data.lehrpersonen.map((lp) => ({
    ...lp,
    stats: calculateLehrpersonStats(lp.id, data),
  }));

  const unterbelastet = lehrpersonenStats.filter(
    (lp) => lp.stats.differenz < -2
  ).length;
  const ausgeglichen = lehrpersonenStats.filter(
    (lp) => Math.abs(lp.stats.differenz) <= 2
  ).length;
  const ueberbelastet = lehrpersonenStats.filter(
    (lp) => lp.stats.differenz > 2
  ).length;

  // Class statistics
  const classesByStufe = data.klassen.reduce((acc, kl) => {
    acc[kl.stufe] = (acc[kl.stufe] || 0) + 1;
    return acc;
  }, {} as Record<number, number>);

  const avgStudentsPerClass =
    data.klassen.length > 0
      ? (totalSchueler / data.klassen.length).toFixed(1)
      : "0";

  // Subject statistics
  const subjectUsage = data.faecher.map((fach) => {
    const zuteilungenCount = data.zuteilungen.filter(
      (zt) => zt.fachId === fach.id
    ).length;
    const totalLektionen = data.zuteilungen
      .filter((zt) => zt.fachId === fach.id)
      .reduce((sum, zt) => sum + zt.lektionen, 0);
    return {
      ...fach,
      zuteilungenCount,
      totalLektionen,
    };
  });

  const renderOverviewReport = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Lehrpersonen</p>
              <p className="text-3xl font-bold mt-1">{totalLehrpersonen}</p>
            </div>
            <Users className="w-12 h-12 text-blue-200 opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Klassen</p>
              <p className="text-3xl font-bold mt-1">{totalKlassen}</p>
            </div>
            <GraduationCap className="w-12 h-12 text-green-200 opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Schüler Total</p>
              <p className="text-3xl font-bold mt-1">{totalSchueler}</p>
            </div>
            <Users className="w-12 h-12 text-purple-200 opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Zuteilungen</p>
              <p className="text-3xl font-bold mt-1">{totalZuteilungen}</p>
            </div>
            <FileText className="w-12 h-12 text-orange-200 opacity-80" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Arbeitsbelastung Verteilung
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="font-medium text-gray-900">Unterbelastet</span>
              </div>
              <span className="text-2xl font-bold text-red-600">
                {unterbelastet}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium text-gray-900">Ausgeglichen</span>
              </div>
              <span className="text-2xl font-bold text-green-600">
                {ausgeglichen}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <TrendingUp className="w-5 h-5 text-orange-600" />
                <span className="font-medium text-gray-900">Überbelastet</span>
              </div>
              <span className="text-2xl font-bold text-orange-600">
                {ueberbelastet}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Klassen pro Stufe
          </h3>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5, 6].map((stufe) => {
              const count = classesByStufe[stufe] || 0;
              const percentage =
                totalKlassen > 0 ? (count / totalKlassen) * 100 : 0;
              return (
                <div key={stufe} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700">
                      Stufe {stufe}
                    </span>
                    <span className="text-gray-500">{count} Klassen</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  const renderWorkloadReport = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Detaillierte Arbeitsbelastung
          </h3>
          <button
            onClick={() => exportToExcel(data)}
            className="inline-flex items-center space-x-2 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>Excel Export</span>
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vertr. Lekt.
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Eff. Soll
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ist
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Differenz
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Auslastung
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {lehrpersonenStats
                .sort((a, b) => b.stats.differenz - a.stats.differenz)
                .map((lp) => {
                  const statusColor =
                    lp.stats.differenz < -2
                      ? "text-red-600 bg-red-50"
                      : lp.stats.differenz > 2
                      ? "text-orange-600 bg-orange-50"
                      : "text-green-600 bg-green-50";
                  const statusIcon =
                    lp.stats.differenz < -2 ? (
                      <AlertCircle className="w-4 h-4" />
                    ) : lp.stats.differenz > 2 ? (
                      <TrendingUp className="w-4 h-4" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    );
                  const statusText =
                    lp.stats.differenz < -2
                      ? "Unterbelastet"
                      : lp.stats.differenz > 2
                      ? "Überbelastet"
                      : "Ausgeglichen";

                  return (
                    <tr
                      key={lp.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">
                          {lp.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                        {lp.solllektionen}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                        {lp.stats.soll.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                        {lp.stats.ist.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <span
                          className={cn(
                            "font-semibold",
                            lp.stats.differenz < -2
                              ? "text-red-600"
                              : lp.stats.differenz > 2
                              ? "text-orange-600"
                              : "text-green-600"
                          )}
                        >
                          {lp.stats.differenz > 0 ? "+" : ""}
                          {lp.stats.differenz.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                        {lp.stats.prozent.toFixed(1)}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={cn(
                            "inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium",
                            statusColor
                          )}
                        >
                          {statusIcon}
                          <span>{statusText}</span>
                        </span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderClassesReport = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="text-center">
            <p className="text-gray-500 text-sm">Durchschnitt SuS/Klasse</p>
            <p className="text-4xl font-bold text-gray-900 mt-2">
              {avgStudentsPerClass}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="text-center">
            <p className="text-gray-500 text-sm">Größte Klasse</p>
            <p className="text-4xl font-bold text-gray-900 mt-2">
              {Math.max(...data.klassen.map((kl) => kl.bestand), 0)}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="text-center">
            <p className="text-gray-500 text-sm">Kleinste Klasse</p>
            <p className="text-4xl font-bold text-gray-900 mt-2">
              {data.klassen.length > 0
                ? Math.min(...data.klassen.map((kl) => kl.bestand))
                : 0}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Klassen Details
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Klasse
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stufe
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Profil
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SuS
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Zuteilungen
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Klassenlehrer
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.klassen.map((kl) => {
                const zuteilungenCount = data.zuteilungen.filter(
                  (zt) => zt.klasseId === kl.id
                ).length;
                const klassenlehrer = kl.klassenlehrerId
                  ? data.lehrpersonen.find((lp) => lp.id === kl.klassenlehrerId)
                  : null;

                return (
                  <tr
                    key={kl.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{kl.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {kl.stufe}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {kl.profil}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {kl.bestand}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {zuteilungenCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {klassenlehrer ? klassenlehrer.name : "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderSubjectsReport = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Fachbelegung Statistik
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kürzel
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fachname
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Zuteilungen
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Lektionen
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  HK Verfügbar
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {subjectUsage
                .sort((a, b) => b.zuteilungenCount - a.zuteilungenCount)
                .map((fach) => {
                  const hkStufen = Object.entries(fach.hk)
                    .filter(([_, hasHk]) => hasHk)
                    .map(([stufe]) => stufe)
                    .join(", ");

                  return (
                    <tr
                      key={fach.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {fach.kuerzel}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">
                          {fach.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                        {fach.zuteilungenCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                        {fach.totalLektionen}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {hkStufen || "-"}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Report Type Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <PieChart className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Berichte & Analysen
            </h2>
          </div>
          <button
            onClick={() => exportToExcel(data)}
            className="inline-flex items-center space-x-2 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>Excel Export</span>
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {reportTypes.map((report) => {
            const Icon = report.icon;
            return (
              <button
                key={report.type}
                onClick={() => setSelectedReport(report.type)}
                className={cn(
                  "flex flex-col items-center space-y-2 p-4 rounded-lg border-2 transition-all",
                  selectedReport === report.type
                    ? "border-primary-600 bg-primary-50"
                    : "border-gray-200 hover:border-gray-300 bg-white"
                )}
              >
                <div
                  className={cn(
                    "p-3 rounded-lg",
                    selectedReport === report.type
                      ? report.color + " text-white"
                      : "bg-gray-100 text-gray-600"
                  )}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <span
                  className={cn(
                    "text-sm font-medium text-center",
                    selectedReport === report.type
                      ? "text-primary-900"
                      : "text-gray-700"
                  )}
                >
                  {report.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Report Content */}
      {selectedReport === "overview" && renderOverviewReport()}
      {selectedReport === "workload" && renderWorkloadReport()}
      {selectedReport === "classes" && renderClassesReport()}
      {selectedReport === "subjects" && renderSubjectsReport()}
    </div>
  );
};

export default Reports;
